import { useEffect, useState } from 'react';
import { taxPaymentSettingsGateway } from '../index';
import { federalDepositScheduleOptions, filingFrequencyOptions, type EmployerTaxDetails } from '../types';
import { friendlyErrorMessage } from '../../../shared/friendlyError';

/** EPN-154 T2: the employer's own tax details for one jurisdiction, effective-dated (a change adds an entry, past entries stay
 * as history). Account numbers are shown masked and are only ever sent when the person types a new one. No rate, rule or
 * FEIN is entered here, and nothing is verified or submitted to any agency. */
export function EmployerDetailsPanel({ jurisdictionKey, jurisdiction }: { jurisdictionKey: string; jurisdiction: string }) {
  const isFederal = jurisdictionKey === 'federal';
  const [details, setDetails] = useState<EmployerTaxDetails | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [withholding, setWithholding] = useState('');
  const [sui, setSui] = useState('');
  const [schedule, setSchedule] = useState('');
  const [frequency, setFrequency] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    taxPaymentSettingsGateway
      .getEmployerDetails(jurisdictionKey, controller.signal)
      .then(setDetails)
      .catch((err) => {
        if (!controller.signal.aborted) setLoadError(friendlyErrorMessage(err, 'Could not load the employer details.'));
      });
    return () => controller.abort();
  }, [jurisdictionKey]);

  async function save() {
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      const result = await taxPaymentSettingsGateway.saveEmployerDetails(jurisdictionKey, {
        effectiveFrom,
        ...(withholding.trim() ? { withholdingAccountNumber: withholding } : {}),
        ...(sui.trim() ? { suiAccountNumber: sui } : {}),
        ...(isFederal && schedule ? { federalDepositSchedule: Number(schedule) } : {}),
        ...(!isFederal && frequency ? { filingFrequency: Number(frequency) } : {}),
      });
      setDetails(result);
      setWithholding('');
      setSui('');
      setSaved(true);
    } catch (err) {
      setSaveError(friendlyErrorMessage(err, 'Could not save the employer details.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section aria-label={`${jurisdiction} employer details`} style={{ marginTop: 16 }}>
      <h3>{jurisdiction} employer details</h3>
      <p className="muted">
        Record these as your agency paperwork shows them, with the date they take effect. Entries are kept as history and nothing
        here is verified or sent to an agency.
        {!isFederal && ' Account numbers are shown masked; type a new one only to change it.'}
      </p>
      {loadError && <p className="error" role="alert">{loadError}</p>}
      {details && (
        <>
          <div className="profile-field-grid">
            <label>
              Effective Date
              <input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} required />
            </label>
            {isFederal ? (
              <label>
                Deposit Schedule
                <select value={schedule} onChange={(e) => setSchedule(e.target.value)}>
                  <option value="">Keep current</option>
                  {federalDepositScheduleOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </label>
            ) : (
              <>
                <label>
                  Withholding Account Number
                  <input value={withholding} maxLength={50} autoComplete="off" placeholder={details.current?.withholdingAccountNumberMasked ?? 'Not recorded'} onChange={(e) => setWithholding(e.target.value)} />
                </label>
                <label>
                  Unemployment (SUI) Account Number
                  <input value={sui} maxLength={50} autoComplete="off" placeholder={details.current?.suiAccountNumberMasked ?? 'Not recorded'} onChange={(e) => setSui(e.target.value)} />
                </label>
                <label>
                  Filing Frequency
                  <select value={frequency} onChange={(e) => setFrequency(e.target.value)}>
                    <option value="">Keep current</option>
                    {filingFrequencyOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </label>
              </>
            )}
          </div>
          {saveError && <p className="error" role="alert">{saveError}</p>}
          {saved && <p className="muted" role="status">Employer details saved.</p>}
          <div style={{ marginTop: 12 }}>
            <button type="button" onClick={() => void save()} disabled={saving || !effectiveFrom}>
              {saving ? 'Saving…' : 'Save Employer Details'}
            </button>
          </div>

          <h4 style={{ marginTop: 20 }}>History</h4>
          {details.history.length === 0 ? (
            <p className="muted">Nothing has been recorded yet.</p>
          ) : (
            <table className="profile-history-table" aria-label={`${jurisdiction} employer detail history`}>
              <thead>
                <tr>
                  <th scope="col">Effective</th>
                  {isFederal ? <th scope="col">Deposit Schedule</th> : (<><th scope="col">Withholding Account</th><th scope="col">SUI Account</th><th scope="col">Filing Frequency</th></>)}
                </tr>
              </thead>
              <tbody>
                {details.history.map((h) => (
                  <tr key={h.effectiveFrom}>
                    <td>{h.effectiveFrom}{h === details.current ? ' (current)' : ''}</td>
                    {isFederal ? <td>{h.federalDepositSchedule ?? '—'}</td> : (<><td>{h.withholdingAccountNumberMasked ?? '—'}</td><td>{h.suiAccountNumberMasked ?? '—'}</td><td>{h.filingFrequency ?? '—'}</td></>)}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}
